import { useState, useCallback } from 'react'
import type { Group, CreateGroupInput, UpdateGroupInput } from '../model/contracts'

const STORAGE_KEY = 'omni-sight-groups'

function loadGroups(): Group[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function saveGroups(groups: Group[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(groups))
}

export function useGroupsController() {
  const [groups, setGroups] = useState<Group[]>(loadGroups)

  const createGroup = useCallback(
    (input: CreateGroupInput) => {
      const newGroup: Group = {
        id: crypto.randomUUID(),
        name: input.name,
        description: input.description,
        sourceIds: input.sourceIds || [],
        pageIds: input.pageIds || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      const updated = [...groups, newGroup]
      setGroups(updated)
      saveGroups(updated)
    },
    [groups],
  )

  const updateGroup = useCallback(
    (input: UpdateGroupInput) => {
      const updated = groups.map((group) =>
        group.id === input.id
          ? {
              ...group,
              ...input,
              sourceIds: input.sourceIds ?? group.sourceIds,
              pageIds: input.pageIds ?? group.pageIds,
              updatedAt: new Date().toISOString(),
            }
          : group,
      )
      setGroups(updated)
      saveGroups(updated)
    },
    [groups],
  )

  const deleteGroup = useCallback(
    (id: string) => {
      const updated = groups.filter((group) => group.id !== id)
      setGroups(updated)
      saveGroups(updated)
    },
    [groups],
  )

  return {
    groups,
    createGroup,
    updateGroup,
    deleteGroup,
  }
}
